<template>
  <div id="tender-general">
    <form
      id="general-form"
      data-abide
      novalidate
    >
      <div class="row align-justify general-info">
        <div class="medium-8 large-8 columns">
          <TextField
            id="modules-tenders-edit-general-projectDescription"
            :metaData="fieldMetaData.projectDescription"
            :readonly="false"
            :required="true"
          />

          <TextField
            id="modules-tenders-edit-general-projectLocation"
            :metaData="fieldMetaData.projectLocation"
            :readonly="false"
            :required="true"
          />

          <div class="tender-label emphasis">
            <label class="required-flag">{{ $t('tender.general.projectStart') }}: </label>
            <div class="row align-middle">
              <input
                id="modules-tenders-edit-general-projectStart"
                v-model.number="(fieldMetaData.projectStart || {}).value"
                v-hkldinput
                v-fusioninputcheck="{type: 'integer'}"
                type="text"
                name="projectStart"
                novalidate
                :required="true"
                class="small-2 medium-2 large-2 num_input validity-period zero-margin-left"
                @change="event => updateFieldValue('projectStart', event.target.value)"
              >
              <p class="validity-period-label">
                {{ projectStartLabel }}
              </p>
            </div>
          </div>

          <TextField
            id="modules-tenders-edit-general-estimatedProjectDuration"
            :metaData="fieldMetaData.estimatedProjectDuration"
            :readonly="false"
            :required="true"
          />

          <div
            v-if="closingDate && closingDate.value"
            class="row closing-date-extension"
          >
            <TextField
              id="modules-tenders-edit-general-closingDate"
              class="small-6 medium-6 large-6"
              :metaData="closingDate"
              :readonly="true"
              :required="true"
            />
            <label
              v-if="closingDateExtended"
              class="closing-date-extension-label"
            >
              {{ $t('tender.extensionRequested') }}
            </label>
            <div>
              <a
                v-if="closingDateExtended && isAddendum && isPicView"
                class="fa fa-undo closing-date-extension-undo"
                aria-hidden="true"
                @click="undoClosingDateExtension()"
              />
              <a
                v-if="!closingDateExtended && isAddendum && isPicView"
                class="btn btn--orange closing-date-extension-btn"
                @click="updateFieldValue('closingDateExtended', true)"
              >
                {{ $t('tender.extendClosingDate') }}
              </a>
            </div>
          </div>

          <div id="tender.general.workingHours">
            <working-hours :metaData="fieldMetaData.workingHours" />
          </div>

          <div class="tender-label emphasis noisyWorkingHour-section">
            <input
              id="tender.general.noisyWorkingHours"
              v-model="noisyWorkingHoursChecked"
              v-hkldinput
              type="checkbox"
              class="general-checkbox"
              name="noisyWorkingHours"
              @change="event => onNoisyWorkingHourChecked('noisyWorkingHoursChecked', event)"
            >
            <label
              class="noisy-working-hours"
              for="tender.general.noisyWorkingHours"
            >
              {{ $t('tender.general.noisyWorkingHours') }}
            </label>
          </div>
          <div
            v-if="noisyWorkingHoursChecked"
            id="tender.general.noisyWorkingHours"
          >
            <working-hours
              :isMandatory="noisyWorkingHoursChecked"
              :emptyWorkingHours="emptyNoisyWorkingHours"
              :metaData="fieldMetaData.noisyWorkingHours"
              :hideLabel="true"
              @reset:emptyWorkingHours="emptyNoisyWorkingHours = false"
            />
          </div>
          <TextField
            id="modules-tenders-edit-general-specialWorkingHours"
            :metaData="fieldMetaData.specialWorkingHours"
            :readonly="false"
            :required="false"
          />

          <TextField
            id="modules-tenders-edit-general-paymentTerms"
            :metaData="fieldMetaData.paymentTerms"
            :readonly="false"
            :required="false"
          />

          <div class="tender-label emphasis">
            <label class="required-flag">{{ $t('tender.general.validityPeriod') }}: </label>
            <div class="row align-middle">
              <input
                id="modules-tenders-edit-general-validityPeriod"
                v-model.number="validityPeriod"
                v-hkldinput
                v-fusioninputcheck="{type: 'integer'}"
                type="text"
                name="validityPeriod"
                required
                class="small-2 medium-2 large-2 num_input validity-period zero-margin-left"
                @change="event => updateFieldValue('validityPeriod', event.target.value)"
              >
              <p class="validity-period-label">
                {{ $i18n.t('tender.general.validityPeriodLabel') }}
              </p>
            </div>
          </div>

          <div class="tender-label emphasis">
            <input
              id="tender.general.onsiteVisitRequired"
              v-model="onsiteVisitRequired"
              v-hkldinput
              type="checkbox"
              :disabled="isAddendum && !closingDateExtended"
              class="general-checkbox"
              name="onsiteVisitRequired"
              @change="event => onsiteVisitRequiredChanged(event.target.checked)"
            >
            <label
              class="onsite-visit"
              for="tender.general.onsiteVisitRequired"
            >
              {{ $t('tender.general.onsiteVisitRequired') }}
            </label>
          </div>

          <div class="tender-label emphasis">
            <input
              id="tender.general.optionalItems"
              v-model="optionalItemsChecked"
              v-hkldinput
              type="checkbox"
              class="general-checkbox"
              name="optionalItems"
              @change="event => updateOptionalItemChecked(event.target.checked)"
            >
            <label
              class="optional-checkbox"
              for="tender.general.optionalItems"
            >
              {{ $t('tender.general.optionalItems') }}:
            </label>
          </div>
          <template v-if="optionalItemsChecked">
            <div
              v-for="(item, index) in optionalItems"
              :key="index"
            >
              <div class="row align-middle optional-items">
                <div class="small-1 middle-1 large-1 optional-items-checkbox">
                  <input
                    :id="item.name"
                    v-hkldinput
                    type="checkbox"
                    :checked="item.selected"
                    :name="item.name + ' Required'"
                    @change="event => onOptionalItemCheckBoxChanged(index, event.target.checked)"
                  >
                </div>
                <label class="small-4 middle-4 large-4 columns">{{ item.name }}</label>
                <div class="small-2 middle-2 large-2 columns">
                  <input
                    :id="item.name"
                    v-hkldinput
                    v-fusioninputcheck="getCheckRule(item)"
                    class="num_input no-padding-right-columns"
                    :class="{ 'error-highlight': noItemSelected || invalidOptionalItems[index] }"
                    type="text"
                    :name="item.name"
                    :disabled="!item.selected"
                    :value="item.value"
                    @change="event => onOptionalItemValueChanged(index, event.target.value)"
                  >
                </div>
                <label class="small-4 middle-4 large-4 columns">{{ defaultOptionalItems[index].text.trim() }}</label>
              </div>
            </div>
          </template>

          <template v-if="isTender">
            <div class="tender-label emphasis">
              <label class="required-flag">{{ $t('tender.general.conditionsOfContracts.label') }}: </label>
            </div>
            <div
              v-if="isSiteLoaded && cocI18nValue"
              ref="conditionsOfContracts"
              class="tender-conditions-of-contracts"
            >
              <div
                v-for="(value, key) in cocI18nValue"
                :key="`cocI18n_${key}`"
                class="row align-middle coc-items"
              >
                <label class="small-1 middle-1 large-1 coc-items-checkbox">
                  <input
                    :id="`coc-input-${key}`"
                    type="radio"
                    name="tender.general.conditionsOfContracts"
                    :value="key|uppercase"
                    :checked="checkConditionOfContracts(key)"
                    @click="event => onConditionsOfContractsChanged(key)"
                  >
                </label>
                <label
                  :for="`coc-input-${key}`"
                  class="small-11 middle-11 large-11 columns"
                  v-html="getCocItemByType(key)"
                />
              </div>
              <label
                v-if="cocFileRequired"
                class="small-11 middle-11 large-11 columns coc-items"
              >
                <i class="reminder"> {{ $t('tender.general.conditionsOfContracts.minFilesHint') }} </i>
              </label>
              <FileUpload
                v-if="cocFileRequired"
                ref="cocStandardAttachmentFileUpload"
                :attachment.sync="cocStandardAttachment"
                :resourceId="general.id"
                :metaData="{category: `${TENDER_CONDITIONS_OF_CONTRACTS}`}"
                :minFiles="1"
              />
            </div>
          </template>
        </div>
        <div class="medium-4 large-4 columns general-view-tab-right">
          <div class="tender-label emphasis">
            <label class="prNumber-label">{{ $t('tender.general.prNumber') }}:</label>
          </div>
          <div v-if="isAddendum || !isPicView">
            <p id="modules-tenders-edit-general-prNumber">
              {{ fieldMetaData.prNumber.value }}
            </p>
          </div>
          <div
            v-else
            class="row align-left align-middle pr-number-edit-margin"
          >
            <div class="small-6 middle-6 large-4 columns">
              <input
                id="tender-general-prNumber"
                v-model="prNumber"
                v-fusioninputcheck="{type: 'integer', required: prNumber}"
                type="text"
                name="prNumber"
                class="add-prNumber zero-margin-left"
                data-validator="validatePrNo"
                @keyup.enter.prevent="addPRNo()"
                @change="handlePrNoUpdate"
              >
            </div>
            <button
              id="modules-tenders-edit-general-pr-add"
              role="add-button"
              class="btn btn--darkblue btn-add"
              name="addPrNo"
              type="button"
              :disabled="disablePrNumber"
              @click="addPRNo"
            >{{ $t('action.add') }}</button>
            <button
              v-show="general.prNumber && !isAddendum"
              id="modules-tenders-edit-general-pr-remove"
              role="remove-pr-button"
              class="btn btn--darkblue btn-remove"
              name="removePR"
              type="button"
              @click="removeContractData()"
            >{{ $t('action.removePR') }}</button>
          </div>

          <div
            id="pr-window"
            class="pr-info"
          >
            <div class="tender-label emphasis">
              <label for="tender.general.budget">{{ $t('tender.general.estimatedBudget') }}:</label>
            </div>
            <div>
              <span
                id="tender.general.budget"
                class="pr-data"
              >
                {{ budget | formatCurrency({currencyCode}) }}
              </span>
            </div>

            <TextField
              id="modules-tenders-edit-general-creator"
              :metaData="fieldMetaData.creator"
              :readonly="true"
            />

            <TextField
              id="modules-tenders-edit-general-manager"
              :metaData="fieldMetaData.manager"
              :readonly="true"
            />

            <TextField
              id="modules-tenders-edit-general-site"
              :metaData="site"
              :readonly="true"
            />
            <div class="tender-label emphasis pr-info">
              <label for="tender.general.invitedVendors">{{ $t('tender.general.vendors') }} ({{ (bids) ? Object.entries(bids).length : '-' }}):</label>
            </div>
            <ul class="vendor-list">
              <li
                v-for="bid in bids"
                :key="bid.id"
              >
                {{ deep(bid, 'company.name') || '' }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <!-- Popup for displaying contract data -->
      <ContractData
        ref="contractDialog"
        :contractData.sync="contractData"
      />
    </form>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapState, mapMutations, mapGetters } from 'vuex'
import { utils as formUtils } from '@/plugin/InputChange'
import ContractData from '@/modules/tenders/ContractData'
import FileUpload from '@/modules/FileUpload'
import TextField from '@/modules/tenders/TextField'
import WorkingHours from '@/modules/tenders/WorkingHours'
import EventBus from '@/utils/eventBus'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import dateUtil from '@/utils/dateUtil'
import userHelper from '@/utils/userHelper'
import termsOfConditionsMixin from '@/modules/tenders/termsOfConditionsMixin'
import { dateFormat } from '@/utils/regex'
import { getDepartmentByLocationCode } from '@/api/locations/request'
import { saveAttachment } from '@/api/docs/request'
import { getVendorDetailByIdAndSite } from '@/api/users/request'
import { TENDER_CONDITIONS_OF_CONTRACTS } from '@/api/tenders/consts'
import { validatePR } from '@/api/tenders/request'
import { defaultTenderValidityPeriod } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getPurchaseDetail } from '@/api/purchases/request'

export default {
  name: 'TenderGeneral',
  components: {
    ContractData,
    TextField,
    WorkingHours,
    FileUpload,
  },
  mixins: [termsOfConditionsMixin],
  props: {
    isTender: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    const itqOptionalItems = [
      { name: 'Retention', selected: false, text: '%', value: '' },
      { name: 'Defects Liability Period', selected: false, text: 'Month(s)', value: '' },
    ]
    return {
      deep,
      itqOptionalItems,
      optionalItemsChecked: false,
      contractData: {},
      prNumber: '',
      validityPeriod: defaultTenderValidityPeriod,
      emptyNoisyWorkingHours: false,
      optionalItemsInvalid: false,
      noItemSelected: false,
      invalidOptionalItems: [],
      originalOnsiteVisitRequired: null,
      TENDER_CONDITIONS_OF_CONTRACTS,
      cocStandardAttachment: {
        files: [],
      },
    }
  },
  computed: {
    ...mapGetters('tenders', ['isAddendum', 'isOldTerm', 'isPicView']),
    ...mapState('tenders', {
      general: state => state.currentTender.general,
      bids: state => state.currentTender.bids,
      fieldMetaData (state) {
        return this.buildFieldMetaDataForGeneral(this.general)
      },
      site (state) {
        return {
          fieldName: 'siteId',
          value: (state.currentTender.purchaseRequisition || {}).siteId,
        }
      },
      budget (state) {
        return (state.currentTender.purchaseRequisition || {}).budget
      },
      currencyCode (state) {
        return lodash.get(state, 'currentTender.purchaseRequisition.currencyCode', '')
      }}),
    tenderOptionalItems () {
      return [
        { name: 'Retention', selected: false, text: '%', value: '' },
        { name: 'Defects Liability Period', selected: false, text: 'Month(s)', value: '' },
        { name: 'Liquidated Damages', selected: false, text: this.$t('tender.general.currencyPerDay', [this.currencyCode]), value: '' },
        { name: 'Surety Bond', selected: false, text: "% of contract sum - round to nearest '000", value: '' },
      ]
    },
    defaultOptionalItems () {
      return (this.isTender) ? this.tenderOptionalItems : this.itqOptionalItems
    },
    isSiteLoaded () {
      return this.general.site
    },
    closingDateExtended () {
      return (this.fieldMetaData.closingDateExtended || {}).value
    },
    noisyWorkingHoursChecked () {
      const field = (this.fieldMetaData || {}).noisyWorkingHoursChecked
      return field && field.value
    },
    onsiteVisitRequired () {
      const field = (this.fieldMetaData || {}).onsiteVisitRequired
      return field && field.value
    },
    optionalItems () {
      const optionalItems = ((this.fieldMetaData || {}).optionalItems || {}).value
      if (!optionalItems || optionalItems.length <= 0) {
        return this.defaultOptionalItems
      }
      return (this.fieldMetaData.optionalItems || {}).value
    },

    closingDate () {
      const closingDate = Object.assign({}, this.fieldMetaData.closingDate)
      closingDate.value = this.formatClosingDate(closingDate.value)
      return closingDate
    },

    tenderType () {
      return (this.fieldMetaData.tenderType || {}).value
    },
    cocFileRequired () {
      const type = lodash.toLower(lodash.get(this.general, 'conditionsOfContracts'))
      const cocValue = lodash.get(this.cocI18nValue, type, null)
      return Array.isArray(cocValue)
    },
    disablePrNumber () {
      const prNumber = this.prNumber
      return !(prNumber && prNumber.length >= 1 && !isNaN(prNumber) && (parseFloat(prNumber) || 0) === parseFloat(prNumber))
    },
  },
  watch: {
    fieldMetaData: 'fieldMetaDataChanged',
  },
  async mounted () {
    EventBus.$on('cleanContractData', this.cleanContractData)
    EventBus.$on('setContractData', this.setContractData)
    this.$nextTick(() => {
      // Handle the historical data when the tender is DRAFT
      this.setCocWhenOneOption()
    })
    Foundation.Abide.defaults.validators.validatePrNo =
      () => ((this.fieldMetaData || {}).prNumber || {}).value || false
  },

  methods: {
    ...mapMutations('tenders', {
      updateTenderField: mutationTypes.TENDER_UPDATE_FIELD,
      updateTenderPathField: mutationTypes.TENDER_UPDATE_PATH_FIELD,
      updateDepartment: mutationTypes.TENDER_UPDATE_DEPARTMENT,
    }),
    setCocWhenOneOption () {
      const cocOptionKeys = Object.keys(this.cocI18nValue)
      if (cocOptionKeys.length === 1 && this.general.conditionsOfContracts === null) {
        this.updateFieldValue('conditionsOfContracts', cocOptionKeys[0].toUpperCase())
      }
    },
    fieldMetaDataChanged (val) {
      this.syncOptionalItemsChecked(false)
    },
    isEmptyWorkingHours (hourType) {
      return (((this.fieldMetaData || {})[hourType] || {}).value || []).length === 0
    },

    abideForm () {
      if (this.abide) {
        this.abide.destroy()
        delete this.abide
      }
      this.form = $('#general-form')
      this.form.on('submit', (ev) => {
        ev.preventDefault()
      })
      this.abide = new Foundation.Abide(this.form, {
        liveValidate: false,
      })
    },
    updateOptionalItemChecked (checked) {
      if (!checked) {
        this.resetOptionalItems()
        this.optionalItemsInvalid = false
        this.noItemSelected = false
      }
    },
    validateOptionalItems () {
      this.invalidOptionalItems = []
      this.optionalItemsInvalid = false
      if (!this.optionalItemsChecked) return
      this.noItemSelected = true
      for (let i = 0; i < this.optionalItems.length; i += 1) {
        const rec = this.optionalItems[i]
        if (rec.selected) {
          this.noItemSelected = false
          if (!rec.value) {
            this.optionalItemsInvalid = true
            this.invalidOptionalItems[i] = true
          } else {
            this.invalidOptionalItems[i] = false
          }
        }
      }
    },

    getCheckRule (item) {
      const checkRule = {
        type: 'decimal',
        required: item.selected,
      }
      if (['Retention', 'Surety Bond'].includes(item.name)) {
        checkRule.type = 'integer'
        checkRule.pattern = (val) => {
          const value = Number(val || '') || 0
          return (value >= 0) && (value <= 100)
        }
      }

      return checkRule
    },

    updateCurrentOptionalText () {
      let optionalItems = lodash.cloneDeep(this.optionalItems)

      if (optionalItems && optionalItems.length) {
        for (let i = 0; i < optionalItems.length; i++) {
          if (optionalItems[i].text !== this.defaultOptionalItems[i].text) {
            optionalItems[i].text = this.defaultOptionalItems[i].text
          }
        }

        this.updateFieldValue('optionalItems', optionalItems)
      }
    },

    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '#tender-general')
      this.emptyNoisyWorkingHours = this.noisyWorkingHoursChecked && this.isEmptyWorkingHours('noisyWorkingHours')
      this.validateOptionalItems()
      this.abideForm()
      this.abide.validateForm()
      const conditionsOfContractsValid = this.validateConditionsOfContracts()

      const noPinkInputs = $('#tender-general input.is-invalid-input').length === 0 &&
        $('#tender-general textarea.is-invalid-input').length === 0
      const fusionInputCheckValid = fusionInputCheckUtil.isAllFieldsValid('#tender-general')
      const allValid = noPinkInputs &&
        fusionInputCheckValid &&
        !this.emptyNoisyWorkingHours &&
        !this.optionalItemsInvalid &&
        !this.noItemSelected &&
        conditionsOfContractsValid

      // * Be careful, the validate was designed to return *invalid* status at the first time mistakenly
      return !allValid
    },

    clearErrorClasses () {
      fusionInputCheckUtil.resetAllFields()
      if ($('#general-form .is-invalid-input').length > 0 && this.abide) {
        this.abide.removeErrorClasses($('#general-form .is-invalid-input'))
      }
      this.emptyNoisyWorkingHours = this.optionalItemsInvalid = this.noItemSelected = false
      this.invalidOptionalItems = []
    },

    formatClosingDate (closingDate) {
      if (!closingDate || isNaN(dateUtil.toMoment(closingDate))) return ''
      const closingDateTime = dateUtil.convertDateToArray(dateUtil.toMoment(closingDate), dateFormat, '-')
      return `${closingDateTime[0]} ${closingDateTime[1]}`
    },

    async getInvalidVendors (siteId) {
      const invalidVendors = []
      await Promise.all(
        Object.values(this.contractData.vendors).map(async vendor => {
          const [vendorDetail] = await getVendorDetailByIdAndSite(vendor.id, siteId)
          if (!vendorDetail || !vendorDetail.email || !vendorDetail.enabled) {
            invalidVendors.push(vendor.name || '')
          }
        }),
      )
      return invalidVendors
    },

    async addPRNo () {
      if (!this.prNumber) {
        return
      }
      this.contractData = await getPurchaseDetail(this.prNumber)
      if (this.contractData && !$.isEmptyObject(this.contractData.vendors)) {
        const siteId = this.contractData.siteId
        const department = await getDepartmentByLocationCode(siteId)
        this.updateDepartment({ department })
        const allowAddPR = await validatePR((this.fieldMetaData.id || {}).value, this.prNumber, this.tenderType, this.contractData.budgetBase, this.contractData.siteId)
        if (!allowAddPR) return
        const invalidVendors = await this.getInvalidVendors(siteId)
        if (invalidVendors.length > 0) {
          notification.fireNotification(false, this.$t('error.sendmail.contactInfoMissing', [ invalidVendors.join('\n\t') ]))
          return
        }
        this.$refs.contractDialog.openModal()
      } else {
        const errorResponse = { status: 400 }
        notification.handleResponseAlert(errorResponse, 'prVendor')
      }
    },

    handlePrNoUpdate () {
      if (!this.prNumber || this.prNumber.trim() === '') {
        this.cleanContractData()
        this.setContractData()
      }
    },

    cleanContractData () {
      this.contractData = {}
      this.prNumber = ''
      this.bids = []
      EventBus.$emit('prchanged')
    },

    setContractData () {
      $('#tender-general-prNumber').removeClass('is-invalid-input')
      this.updateTenderPathField({
        path: ['purchaseRequisition'],
        newValue: this.contractData,
      })
      this.updateTenderPathField({
        path: ['bids'],
        newValue: this.getVendorList(),
      })
      this.updateTenderPathField({
        path: ['general', 'prNumber'],
        newValue: this.contractData.number,
      })
      this.updateTenderPathField({
        path: ['general', 'prDescription'],
        newValue: this.contractData.description,
      })
      this.updateTenderPathField({
        path: ['general', 'site'],
        newValue: this.contractData.siteId,
      })
      formUtils.setDirty()
      this.prNumber = ''
      this.setCocWhenOneOption()
      EventBus.$emit('prchanged')
    },

    removeContractData () {
      this.cleanContractData()

      this.updateTenderPathField({
        path: ['purchaseRequisition'],
        newValue: {},
      })
      this.updateTenderPathField({
        path: ['bids'],
        newValue: [],
      })
      this.updateTenderPathField({
        path: ['general', 'prNumber'],
        newValue: null,
      })
      this.updateTenderPathField({
        path: ['general', 'prDescription'],
        newValue: null,
      })
      this.updateTenderPathField({
        path: ['general', 'site'],
        newValue: null,
      })
      this.updateDepartment({department: null})
      this.updateFieldValue('conditionsOfContracts', null)
      formUtils.setDirty()
    },

    getVendorList () {
      let vendorsMap = {}
      if (this.contractData.vendors) {
        vendorsMap = this.contractData.vendors
      }
      return Object.keys(vendorsMap).map(id => ({
        vendorId: id,
        name: vendorsMap[id].name,
        company: vendorsMap[id],
      }))
    },
    updateFieldValue (fieldName, newValue) {
      this.updateTenderField({ sectionName: 'general', fieldName, newValue })
    },
    onCheckBoxChanged (fieldName, newValue) {
      this.updateFieldValue(fieldName, newValue)
    },
    onsiteVisitRequiredChanged (newValue) {
      if (this.originalOnsiteVisitRequired === null) {
        this.originalOnsiteVisitRequired = this.onsiteVisitRequired
      }
      this.onCheckBoxChanged('onsiteVisitRequired', newValue)
      if (!newValue) {
        this.resetOnsiteVisitDates()
      }
    },
    resetOnsiteVisitDates () {
      for (let i = 0; i < this.bids.length; i += 1) {
        this.updateTenderPathField({
          path: ['bids', i, 'visitDate'],
          newValue: null,
        })
      }
    },
    undoClosingDateExtension () {
      if (this.originalOnsiteVisitRequired !== null) {
        this.onCheckBoxChanged('onsiteVisitRequired', this.originalOnsiteVisitRequired)
      }
      this.updateFieldValue('closingDateExtended', false)
    },
    onNoisyWorkingHourChecked (fieldName, event) {
      const checked = event.target.checked
      if (!checked) {
        const path = ['general', 'noisyWorkingHours']
        this.updateTenderPathField({
          path,
          newValue: [],
        })
      }
      this.onCheckBoxChanged(fieldName, checked)
    },
    onOptionalItemCheckBoxChanged (index, checked) {
      this.updateOptionalItem(index, checked, null)
      this.syncOptionalItemsChecked(true)
    },
    onOptionalItemValueChanged (index, value) {
      this.updateOptionalItem(index, true, value)
    },
    syncOptionalItemsChecked (force = true) {
      let optionalItemsChecked = false
      Object.values(this.optionalItems).forEach((rec) => {
        if (rec.selected) {
          optionalItemsChecked = true
        }
      })
      if (force || optionalItemsChecked) {
        this.optionalItemsChecked = optionalItemsChecked
      }
    },
    resetOptionalItems () {
      const optionalItems = lodash.cloneDeep(this.optionalItems)
      optionalItems.forEach(item => {
        item.selected = false
        item.value = ''
      })
      this.updateFieldValue('optionalItems', optionalItems)
    },

    updateOptionalItem (index, checked, value = '') {
      const optionalItems = lodash.cloneDeep(this.optionalItems)
      optionalItems[index].selected = checked
      if (!checked) {
        optionalItems[index].value = ''
      } else {
        optionalItems[index].value = value
      }
      this.updateFieldValue('optionalItems', optionalItems)
    },

    async onConditionsOfContractsChanged (type) {
      this.$refs.conditionsOfContracts.classList.remove('is-invalid-input')
      this.updateFieldValue('conditionsOfContracts', type.toUpperCase())
    },

    buildFieldMetaDataForGeneral (general) {
      if (Object.keys(general).length === 0) return {}
      const metadata = Object.keys(general)
        .reduce((partialData, fieldName) => ({
          ...partialData,
          [fieldName]: {
            sectionName: 'general',
            fieldName,
            value: general[fieldName],
            text: null,
          },
        }), {})
      metadata.creator.value = userHelper.getUserName(deep(general, 'creator.username') || '', deep(general, 'creator.displayName'))
      metadata.manager.value = userHelper.getUserName(deep(general, 'manager.username') || '', deep(general, 'manager.displayName'))
      metadata.projectStart.text = this.projectStartLabel

      if (metadata.validityPeriod.value === null) {
        metadata.validityPeriod.value = defaultTenderValidityPeriod
        this.updateFieldValue('validityPeriod', defaultTenderValidityPeriod)
      }
      this.validityPeriod = metadata.validityPeriod.value
      return metadata
    },
    async saveCocStandardAttachment () {
      if (!this.isTender) return
      if (this.cocFileRequired) {
        await saveAttachment(this.cocStandardAttachment)
      }
    },
    validateConditionsOfContracts () {
      if (!this.isTender) return true
      if (this.general.conditionsOfContracts) {
        return !this.cocFileRequired || this.$refs.cocStandardAttachmentFileUpload.validateMinFiles()
      }
      this.$refs.conditionsOfContracts.classList.add('is-invalid-input')
      return false
    },
    checkConditionOfContracts (type) {
      return this.general.conditionsOfContracts === lodash.toUpper(type)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/tender/edit";
.columns {
  padding-left: 0;
}
.term-header {
  margin-bottom: 0.625rem

}

.term-box {
  display: flex;
}
.term {
  padding-left: 10px;
  margin-bottom: 0px;
}

.general-view-tab-right {
  padding-left: 24px;
  background: white;
}
.prNumber-label {
  margin-top: 5px;
}

.general-checkbox {
  margin-top: 4px;
  margin-bottom: 0px;
  font-size: 16px;
}

.row {
  width: 100%;
  max-width: 100%;
}

.label-font {
  height: 20px;
  font-family: Verdana;
  font-size: 14px;
  line-height: 1.43;
  color: #5f7988;
}

.num_input {
  margin-bottom: 5px;
  width: 90%;
  min-width: 40px;
}

.validity-period {
  margin-left: 15px;
  margin-right: 10px;
}

.no-padding-right-columns {
  padding-right: 0;
}
.no-padding-left-columns {
  padding-left: 0;
}

.optional-checkbox {
  margin-left: 0;
}
.optional-items, .coc-items {
  padding-left: 25px;
  label {
    line-height: 20px;
  }
}
.coc-items {
  align-items: start;
  .reminder {
    font-size: 12px;
    color: #878787;
  }
  ::v-deep .flex{
    display: inline-flex;
    align-content: flex-start;
  }
}
.optional-items-checkbox, .coc-items-checkbox {
  width: 20px;
  max-width: 20px;
  input [type='checkbox'] {
    font-size: 16px;
  }
}

.add-prNumber {
  margin-left: 15px;
  margin-bottom: 0;
  margin-top: 3px;
}
.pr-data {
  display: inline-block;
  margin-bottom: 15px;
  color: #0C3A58;
}
.pr-number-edit-margin {
  margin-bottom: 25px;
}

.vendor-list {
  list-style-type: decimal;
  list-style-position: outside;
  margin: 0 0 5px 0;
  padding-left: 20px;
  color: #0C3A58;
}

#tender-general {
  background: #f8f8f8;
  font-size: 14px;
  label {
    margin: 5px 0 0 0;
    line-height: 2;
  }
  .optional-items label {
    margin-bottom: 5px;
  }
  .noisyWorkingHour-section {
    label {
      margin-top: 0;
    }
  }
}
#modules-tenders-edit-general-prNumber{
  margin-top: -3px;
  color: #0C3A58;
}
.btn-add, .btn-remove {
  line-height: 17px;
  display: inline-block;
  vertical-align: middle;
  margin-left: 0;
  margin-bottom: 1px;
  &:hover {
    color: #fff;
  }
}
.btn-remove {
  margin-left: 10px;
}
.noisy-working-hours, .onsite-visit {
  margin: 3px 0 2px 0;
}

#tender-general input.is-invalid-input,
#tender-general #tender\.general\.projectDescription.is-invalid-input:not(:focus){
  background-color: #f9ecea!important;
}
#modules-tenders-edit-general-site label{
  margin-bottom: 2px;
}

.validity-period-label {
  margin: 5px;
}

.closing-date-extension-btn {
  display: inline-block;
  margin-top: 13px;
  &:hover {
    color: #fff;
  }
}

.closing-date-extension-label {
  margin: 25px 10px 0 0!important;
  color: #e69500;
}
.closing-date-extension-undo {
  margin: 32px 10px 0 5px;
  color: #e69500;
}
.closing-date-extension {
  margin: 0;
  .text-field {
    margin: 0 15px 0 0;
  }
}
.zero-margin-left {
  margin-left: 0
}
.tender-conditions-of-contracts{
  min-height: 40px;
}
</style>
